"use client";

import { AlertTriangle, CheckCircle, User, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import type { Tournament, CustomRegistrationFieldDto } from "@/entities/tournament/model/types";
import { useUserProfile } from "@/hooks/useAuth";
import { useOrganizationRestriction } from "@/hooks/use-organization-restriction";
import { TempTeamCreateDialog } from "@/features/tournament/temp-team-create-dialog";
import { useTournamentRegistration } from "@/entities/tournament/model/use-tournament-registration";
import { formatMinRequiredRankRu, isAccountRankAllowed } from "@/shared/lib/riot-rank";
import {
  filterAccountsForTournamentRegion,
  normalizeTournamentRegionToPlatform,
} from "@/shared/lib/riot-region-compat";
import { Button } from "@/shared/ui/buttons/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { Label } from "@/shared/ui/forms/label";
import { Input } from "@/shared/ui/inputs/input";
import { Textarea } from "@/shared/ui/inputs/textarea";
import { Alert, AlertDescription } from "@/shared/ui/feedback/alert";

import {
  RegistrationRoleFields,
  type RegistrationRoleFieldsValue,
} from "./registration-role-fields";
import { isTeamEligibleForRegistration } from "./lib/team-size-eligibility";

interface TournamentRegistrationFormProps {
  tournament: Tournament;
  onSuccess: () => void;
  /**
   * В модалке заголовок и описание уже есть в шапке диалога — карточка «какой это тип
   * регистрации» дублировала бы их. На отдельной странице она, наоборот, нужна.
   */
  showFormatCard?: boolean;
}

export function TournamentRegistrationForm({
  tournament,
  onSuccess,
  showFormatCard = true,
}: TournamentRegistrationFormProps) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const { data: user } = useUserProfile();
  const organizationId = tournament.organizationId ?? undefined;
  const { isRestricted } = useOrganizationRestriction(organizationId);
  const {
    selectedTeam,
    selectedRiotAccount,
    mainRole,
    secondaryRole,
    desiredRole,
    playerNote,
    customFieldValues,
    setCustomFieldValues,
    teams,
    riotAccounts,
    isLoading,
    error,
    success,
    dataLoading,
    setSelectedTeam,
    setMainRole,
    setSecondaryRole,
    setDesiredRole,
    setPlayerNote,
    register,
    loadData,
  } = useTournamentRegistration(tournament);

  const customRegistrationFields = (tournament as { customRegistrationFields?: CustomRegistrationFieldDto[] }).customRegistrationFields ?? [];

  const isDraftMode = tournament?.format === "Draft";
  // Arena — командный (temp-команды по 3): UI выбора/создания команды, не solo.
  const isClassicMode =
    tournament?.format === "Classic" ||
    tournament?.format === "SoloOneVsOne" ||
    tournament?.format === "Arena";
  const requiredTeamSize = tournament?.maxTeamSize || 5;
  // Riot-аккаунт обязателен, только если турнир его требует (как в tournament-actions.tsx).
  // При requireRiotAccount=false пользователь без аккаунта должен уметь зарегистрироваться,
  // иначе форма — тупик «У вас нет привязанных Riot аккаунтов» (KAN-144 #2).
  const requiresRiot =
    (tournament as { requireRiotAccount?: boolean }).requireRiotAccount !== false;
  const isSoloClassic = isClassicMode && requiredTeamSize === 1;
  const needsTeamSelection = isClassicMode && requiredTeamSize > 1;
  // KAN-339: временные («турнирные») команды раньше были доступны только для 2–4, а для 5×5
  // существовала вторая, недописанная реализация — сквады (в сетку не попадали, UI создания
  // не было). Расширили рабочий механизм до 2–5 и удалили сквады. Признак заменяет собой
  // разрозненные проверки `requiredTeamSize < 5`, которые раньше и гейтили фичу, и управляли
  // формулировками.
  const supportsTempTeams = needsTeamSelection && requiredTeamSize <= 5;
  // KAN-1621: `maxMembers` — вместимость ростера, а не основной состав. Команда
  // «5 основных + запасные» имеет maxMembers 10, и проверка `=== requiredTeamSize`
  // скрывала её от капитана, хотя сервер такие команды принимает. Правило вынесено
  // в lib/team-size-eligibility.ts и покрыто тестом.
  const isArenaFormat = tournament?.format === "Arena";
  const captainTeams = needsTeamSelection
    ? teams.filter(
        (team) =>
          team.captainId === user?.id &&
          isTeamEligibleForRegistration(team, requiredTeamSize, isArenaFormat),
      )
    : [];

  const [rankModalOpen, setRankModalOpen] = useState(false);

  // KAN-109: бэкенд требует полный состав (для Arena — ровно requiredTeamSize),
  // поэтому неполную команду блокируем до отправки с явной причиной.
  const selectedTeamObj = needsTeamSelection
    ? captainTeams.find((team) => team.id === selectedTeam)
    : undefined;
  const incompleteTeamReason =
    selectedTeamObj && (selectedTeamObj.memberCount ?? 0) < requiredTeamSize
      ? tUi("litTournamentRegistrationForm.nuzhenPolnyySostav", { size: requiredTeamSize, current: selectedTeamObj.memberCount ?? 0 })
      : null;

  const tournamentRegionPlatform = useMemo(
    () => normalizeTournamentRegionToPlatform(tournament.region),
    [tournament.region],
  );
  const eligibleRiotAccounts = useMemo(
    () => filterAccountsForTournamentRegion(riotAccounts, tournament.region),
    [riotAccounts, tournament.region],
  );

  const requiredRankRaw = tournament?.minRequiredRank as
    | string
    | null
    | undefined;
  const requiredRankRu = formatMinRequiredRankRu(requiredRankRaw);

  const selectedAccount = useMemo(() => {
    if (!selectedRiotAccount) return null;
    return (
      riotAccounts.find((acc) => acc.id === selectedRiotAccount) ?? null
    );
  }, [riotAccounts, selectedRiotAccount]);

  const rankBlockReason = useMemo(() => {
    if (!requiredRankRaw) return null;
    if (!selectedAccount) return null;
    const accountRank = selectedAccount.rank || "UNRANKED";
    const ok = isAccountRankAllowed(accountRank, requiredRankRaw);
    if (ok) return null;
    return tUi("litTournamentRegistrationForm.rangNizheMinimalnogo", { min: requiredRankRu ?? requiredRankRaw, rank: accountRank });
  }, [requiredRankRaw, requiredRankRu, selectedAccount, tUi]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isRestricted) {
      return;
    }

    if (requiresRiot && !selectedRiotAccount) {
      // KAN-147 #4: без фидбэка кнопка «не работает» молча.
      toast.error(tToast("tournamentRegistrationForm.vyberiteRiotAkkauntDlya"));
      return;
    }

    if (needsTeamSelection && !selectedTeam) {
      toast.error(tToast("tournamentRegistrationForm.vyberiteKomanduDlyaRegistratsii"));
      return;
    }

    if (incompleteTeamReason) {
      toast.error(incompleteTeamReason);
      return;
    }

    if (isDraftMode && !mainRole) {
      toast.error(tToast("tournamentRegistrationForm.ukazhiteOsnovnuyuRol"));
      return;
    }

    if (rankBlockReason) {
      setRankModalOpen(true);
      return;
    }

    const requiredCustomFields = customRegistrationFields.filter((f) => f.required);
    if (requiredCustomFields.length > 0) {
      const missing = requiredCustomFields.filter(
        (f) => !((customFieldValues[f.key] ?? "").trim().length > 0),
      );
      if (missing.length > 0) {
        toast.error(tToast("tournamentRegistrationForm.zapolniteVseObyazatelnyeDopolnitelnye"));
        return;
      }
    }

    const ok = await register();
    if (!ok) {
      const msg = String(error ?? "");
      if (msg.includes(tUi("litTournamentRegistrationForm.nedostatochnyyRang")) || msg.includes(tUi("litTournamentRegistrationForm.minimum"))) {
        setRankModalOpen(true);
      }
      return;
    }

    onSuccess();
  };

  if (dataLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-1/3"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <Alert>
        <CheckCircle className="h-4 w-4" />
        <AlertDescription>
          {tUi("tournamentRegistrationForm.vyUspeshnoZaregistrirovanyNa")}
        </AlertDescription>
      </Alert>
    );
  }

  // Check if user can register (минимально)
  const canRegister = () => {
    // Riot-гейты пропускаем, если турнир не требует Riot-аккаунта (KAN-144 #2):
    // иначе пользователь без аккаунта упирается в тупик, хотя кнопка «Участвовать»
    // доступна (tournament-actions.tsx честно учитывает requireRiotAccount).
    if (requiresRiot && riotAccounts.length === 0) {
      return { can: false, reason: tUi("litTournamentRegistrationForm.uVasNetPrivyazannyh") };
    }

    if (requiresRiot && tournamentRegionPlatform && eligibleRiotAccounts.length === 0) {
      return {
        can: false,
        reason: tUi("litTournamentRegistrationForm.netRiotAkkauntovRegiona", { region: tournamentRegionPlatform }),
      };
    }

    if (needsTeamSelection) {
      if (captainTeams.length === 0) {
        return {
          can: false,
          reason: supportsTempTeams
            ? tUi("litTournamentRegistrationForm.soberiteVremennuyuKomanduDlya")
            : tUi("litTournamentRegistrationForm.uVasNetDostupnyh"),
        };
      }
    }

    return { can: true };
  };

  const registrationCheck = canRegister();

  if (!registrationCheck.can) {
    const canCreateTempTeamHere = supportsTempTeams && isClassicMode;
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex flex-col gap-3">
          <div>{registrationCheck.reason}</div>
          {canCreateTempTeamHere && (
            <div className="flex flex-wrap gap-2">
              <TempTeamCreateDialog
                tournamentId={tournament.id ?? ""}
                trigger={
                  <Button variant="outline" size="sm">
                    {tUi("tournamentRegistrationForm.sozdatTurnirnuyuKomandu")}
                  </Button>
                }
                afterCreate={async () => {
                  await loadData();
                }}
              />
              <Button variant="ghost" size="sm" onClick={loadData}>
                {tUi("tournamentRegistrationForm.obnovitSpisok")}
              </Button>
            </div>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <AlertDialog open={rankModalOpen} onOpenChange={setRankModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tUi("tournamentRegistrationForm.rangNizheMinimalnogo")}</AlertDialogTitle>
            <AlertDialogDescription>
              {rankBlockReason ||
                tUi("litTournamentRegistrationForm.trebuetsyaMinimum", { min: requiredRankRu ?? requiredRankRaw ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tUi("tournamentRegistrationForm.ok")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => setRankModalOpen(false)}>
              {tUi("tournamentRegistrationForm.ponyal")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {rankBlockReason ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="whitespace-pre-line">
            {rankBlockReason}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Tournament Type Info */}
      {showFormatCard && (
      <Card className="border-0 bg-muted/30 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            {isDraftMode ? (
              <User className="h-4 w-4" />
            ) : (
              <Users className="h-4 w-4" />
            )}
            {isDraftMode
              ? tUi("litTournamentRegistrationForm.individualnayaRegistratsiya")
              : isSoloClassic
                ? tUi("litTournamentRegistrationForm.individualnayaRegistratsiya1v1")
                : tUi("litTournamentRegistrationForm.registratsiyaKomandy")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {isDraftMode
              ? tUi("litTournamentRegistrationForm.vyRegistriruetesKakIndividualnyy")
              : isSoloClassic
                ? tUi("litTournamentRegistrationForm.vyRegistriruetesNa1v1")
                : tUi("litTournamentRegistrationForm.vyRegistrirueteKomanduNa")}
          </p>
        </CardContent>
      </Card>
      )}

      {/* Riot Account Info - Auto-selected */}
      <div className="space-y-2">
        <Label htmlFor="riot-account">
          {tUi("tournamentRegistrationForm.riotAkkauntAvtomaticheskiVybran")}
        </Label>
        {selectedRiotAccount && riotAccounts.length > 0 && (
          <div className="p-3 rounded-lg bg-muted/30">
            {(() => {
              const account = selectedAccount;
              return account ? (
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {account.gameName}#{account.tagLine}
                  </span>
                  <div className="text-sm text-muted-foreground">
                    {account.region} {account.rank && `• ${account.rank}`}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        )}
        {riotAccounts.length > 1 && (
          <p className="text-xs text-muted-foreground">
            Выбран аккаунт с наивысшим рангом из {riotAccounts.length} доступных
          </p>
        )}
      </div>

      {/* Team Selection (Classic Mode) */}
      {needsTeamSelection && (
        <div className="space-y-2 rounded-xl bg-muted/30 p-4">
          <Label htmlFor="team">
            {supportsTempTeams ? tUi("litTournamentRegistrationForm.komandaNaTurnir") : tUi("litTournamentRegistrationForm.komanda")}
          </Label>
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger>
              <SelectValue
                placeholder={
                  supportsTempTeams
                    ? tUi("litTournamentRegistrationForm.vyberiteKomanduIliSoberite")
                    : tUi("litTournamentRegistrationForm.vyberiteKomandu")
                }
              />
            </SelectTrigger>
            <SelectContent>
              {captainTeams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name} ({team.tag}) - {team.memberCount}/
                  {team.maxMembers} игроков
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {incompleteTeamReason && (
            <p className="text-xs text-destructive">{incompleteTeamReason}</p>
          )}
          {captainTeams.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {supportsTempTeams
                ? tUi("litTournamentRegistrationForm.soberiteVremennuyuKomanduDlya2")
                : tUi("litTournamentRegistrationForm.vyNeKapitanNi")}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {tUi("tournamentRegistrationForm.pokazanyTolkoKomandyV")}
            </p>
          )}
        </div>
      )}

      {/* Роли, капитанство и заметка — тот же блок, что и в правке заявки.
          Держать их в двух копиях уже приводило к расхождению: на одном турнире
          часть игроков оказалась без дополнительной роли просто потому, что
          регистрировалась через другую форму. */}
      {isDraftMode && (
        <RegistrationRoleFields
          value={{
            mainRole,
            secondaryRole,
            wantsCaptain: desiredRole === "Captain",
            playerNote,
          }}
          onChange={(patch: Partial<RegistrationRoleFieldsValue>) => {
            if (patch.mainRole !== undefined) setMainRole(patch.mainRole);
            if (patch.secondaryRole !== undefined) setSecondaryRole(patch.secondaryRole);
            if (patch.wantsCaptain !== undefined)
              setDesiredRole(patch.wantsCaptain ? "Captain" : "Participant");
            if (patch.playerNote !== undefined) setPlayerNote(patch.playerNote);
          }}
          showCaptainAndNote
          idPrefix="new-reg"
        />
      )}

      {customRegistrationFields.length > 0 && (
        <div className="space-y-3 pt-2 rounded-xl bg-muted/30 p-4">
          <p className="text-sm font-medium text-foreground">
            {tUi("tournamentRegistrationForm.dopolnitelnyeVoprosy")}
          </p>
          {customRegistrationFields.map((f) => (
            <div key={f.key} className="space-y-2">
              <Label>
                {f.label}
                {f.required && " *"}
              </Label>
              {f.type === "Text" && (
                <Input
                  value={customFieldValues[f.key] ?? ""}
                  onChange={(e) =>
                    setCustomFieldValues((prev) => ({
                      ...prev,
                      [f.key]: e.target.value,
                    }))
                  }
                  placeholder={f.label}
                />
              )}
              {f.type === "Textarea" && (
                <Textarea
                  className="min-h-[80px]"
                  value={customFieldValues[f.key] ?? ""}
                  onChange={(e) =>
                    setCustomFieldValues((prev) => ({
                      ...prev,
                      [f.key]: e.target.value,
                    }))
                  }
                  placeholder={f.label}
                />
              )}
              {f.type === "Select" && (
                <Select
                  value={customFieldValues[f.key] ?? ""}
                  onValueChange={(v) =>
                    setCustomFieldValues((prev) => ({ ...prev, [f.key]: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={tUi("tournamentRegistrationForm.vyberiteVariant")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(f.options ?? []).map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isRestricted || isLoading || Boolean(incompleteTeamReason)}
        className="w-full"
      >
        {isLoading ? tUi("litTournamentRegistrationForm.registratsiya") : tUi("litTournamentRegistrationForm.zaregistrirovatsya")}
      </Button>
    </form>
  );
}
